import { ProductActivityOvertimeReport } from '@/client/dashboard/types';
import { AccordionTabHeader } from '@/ui/accordion';
import { FlexContainer } from '@/ui/styled-ui';
import { getTimeBasedLabels } from '@/utils/helpers';
import {
  colorBlackMain,
  colorBlueDark,
  colorGrayDark,
  colorGreenChartMiddle,
  colorPurpleText,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  createCustomTooltip,
  hideTooltipForNavigation,
} from '../charts.helper';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const ChartWrapper = styled.div<{ $isMainDashboard?: boolean }>`
  width: 100%;
  ${({ $isMainDashboard }) =>
    $isMainDashboard
      ? `
    height: 100%;
  `
      : `
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: var(--default-padding);
    overflow: hidden;
    flex: 1;
  `}
`;

const ChartContainer = styled.div<{ $isMainDashboard?: boolean }>`
  overflow-x: scroll;
  width: 100%;
  position: relative;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  background: var(--white-main);
  height: 510px;

  ${({ $isMainDashboard }) =>
    $isMainDashboard
      ? `
    min-height: 510px;
    padding: var(--big-padding) var(--default-padding) var(--default-padding);
  `
      : `
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 0;
    padding: var(--default-padding);
  `}
`;

const StyledBarChart = styled(Chart)<{ $fitContainer?: boolean }>`
  width: 100%;
  height: 100%;
  max-height: 100%;
  flex: 1;
  min-width: ${({ $fitContainer }) => ($fitContainer ? '0' : '800px')};
`;

type ActivityBarChartProps = {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  activeAwarenessTraining: boolean;
  activePhishing: boolean;
  activeSecurityCulture: boolean;
  reportData?: ProductActivityOvertimeReport[];
  isLoading?: boolean;
  isMainDashboard?: boolean;
};

const getDataMappedToLabels = (
  reportData: ProductActivityOvertimeReport[] | undefined,
  labels: { [key: number]: string }[],
  isYearly?: boolean,
): {
  label: string;
  value: ProductActivityOvertimeReport | null;
}[] => {
  if (!reportData || !labels) {
    return [];
  }

  if (isYearly) {
    return labels.map((labelObject: { [key: number]: string }) => {
      const matchingItem = reportData?.find((item) => {
        return Object.values(labelObject).includes(item.period);
      });
      return {
        label: Object.values(labelObject)[0],
        value: matchingItem || null,
      };
    });
  } else {
    return labels.map((labelObject: { [key: number]: string }) => {
      const matchingItem = reportData?.find((item) => {
        const month = Number(item?.period?.split('-')[1]) - 1;
        return month === Number(Object.keys(labelObject)[0]);
      });
      return {
        label: Object.values(labelObject)[0],
        value: matchingItem || null,
      };
    });
  }
};

export const ActivityBarChart = ({
  fromDate,
  toDate,
  reportData,
  isLoading,
  activeAwarenessTraining,
  activePhishing,
  activeSecurityCulture,
  isMainDashboard = false,
}: ActivityBarChartProps) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [datasets, setDatasets] = useState<
    {
      label: string;
      value: ProductActivityOvertimeReport | null;
    }[]
  >([]);
  const [isChartDataLoading, setIsChartDataLoading] = useState(false);
  const [datasetVisibility, setDatasetVisibility] = useState({
    courseCompletion: true,
    mailsSent: true,
    surveysAnswered: true,
  });
  const docStyle = getComputedStyle(document.documentElement);
  const textColor = colorGrayDark(docStyle);
  const textColorSecondary = colorBlackMain(docStyle);

  const handleLegendClick = (datasetKey: string) => {
    setDatasetVisibility((prev) => ({
      ...prev,
      [datasetKey]: !prev[datasetKey as keyof typeof prev],
    }));
  };

  const hasNoData =
    !reportData || reportData.length === 0 || datasets.length === 0;

  useEffect(() => {
    if (!reportData) return;
    const performanceOverTimeLabels = getTimeBasedLabels(
      fromDate,
      toDate,
      reportData,
      t,
    );

    if (!performanceOverTimeLabels || !performanceOverTimeLabels.length) return;
    const datasetsData = getDataMappedToLabels(
      reportData,
      performanceOverTimeLabels,
      !fromDate && !toDate,
    );
    setIsChartDataLoading(true);
    setDatasets(datasetsData);
  }, [reportData, fromDate, toDate]);

  useEffect(() => {
    const courseCompletionData =
      datasets
        ?.map((item) => {
          const val = Number(item.value?.completedContent);
          return isNaN(val) ? null : val;
        })
        .filter((val): val is number => val !== null && val !== undefined) ||
      [];

    const mailsSentData =
      datasets
        ?.map((item) => {
          const val = Number(item.value?.phishingEmailsSent);
          return isNaN(val) ? null : val;
        })
        .filter((val): val is number => val !== null && val !== undefined) ||
      [];

    const surveysAnsweredData =
      datasets
        ?.map((item) => {
          const val = Number(item.value?.securityCultureSurveyCompletions);
          return isNaN(val) ? null : val;
        })
        .filter((val): val is number => val !== null && val !== undefined) ||
      [];

    const allValues = [
      ...(activeAwarenessTraining ? courseCompletionData : []),
      ...(activePhishing ? mailsSentData : []),
      ...(activeSecurityCulture ? surveysAnsweredData : []),
    ];

    const maxDataValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    const roundedMax =
      maxDataValue > 0 ? Math.ceil((maxDataValue + 10) / 40) * 40 : 40;

    const allDatasets = [
      ...(activeAwarenessTraining
        ? [
            {
              type: 'bar' as const,
              label: t('dashboard.activity.overTime.courseCompletion'),
              backgroundColor: colorBlueDark(docStyle),
              data: datasets?.map((item) =>
                Number(item.value?.completedContent || 0),
              ),
              hoverBackgroundColor: colorBlueDark(docStyle) + 'CC',
              hidden: !datasetVisibility.courseCompletion,
            },
          ]
        : []),
      ...(activePhishing
        ? [
            {
              type: 'bar' as const,
              label: t('dashboard.activity.overTime.mailsSent'),
              backgroundColor: colorGreenChartMiddle(docStyle) + 'E6',
              data: datasets?.map((item) =>
                Number(item.value?.phishingEmailsSent || 0),
              ),
              hoverBackgroundColor: colorGreenChartMiddle(docStyle) + 'CC',
              hidden: !datasetVisibility.mailsSent,
            },
          ]
        : []),
      ...(activeSecurityCulture
        ? [
            {
              type: 'bar' as const,
              label: t('dashboard.activity.overTime.surveysAnswered'),
              backgroundColor: colorPurpleText(docStyle) + 'E6',
              data: datasets?.map((item) =>
                Number(item.value?.securityCultureSurveyCompletions || 0),
              ),
              hoverBackgroundColor: colorPurpleText(docStyle) + 'CC',
              hidden: !datasetVisibility.surveysAnswered,
            },
          ]
        : []),
    ];

    const data = {
      labels: datasets?.map((item) => item.label),
      datasets: allDatasets,
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      responsive: true,
      layout: {
        padding: {
          top: 5,
          right: 10,
          bottom: 5,
          left: 5,
        },
      },
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: (context: any) =>
            createCustomTooltip(context, t, datasets, false, false),
        },
        legend: {
          display: isMainDashboard,
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 10,
            boxHeight: 10,
            padding: 24,
            font: {
              family: platformFontFamily(docStyle),
              size: 14,
              weight: 500,
            },
            generateLabels: () => {
              const labels = [];
              let datasetIndex = 0;

              if (activeAwarenessTraining) {
                labels.push({
                  text: t('dashboard.activity.overTime.courseCompletion'),
                  fillStyle: datasetVisibility.courseCompletion
                    ? colorBlueDark(docStyle) + 'E6'
                    : colorGrayDark(docStyle),
                  strokeStyle: datasetVisibility.courseCompletion
                    ? colorBlueDark(docStyle) + 'E6'
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 1,
                  hidden: !datasetVisibility.courseCompletion,
                  datasetIndex: datasetIndex++,
                });
              }

              if (activePhishing) {
                labels.push({
                  text: t('dashboard.activity.overTime.mailsSent'),
                  fillStyle: datasetVisibility.mailsSent
                    ? colorGreenChartMiddle(docStyle) + 'E6'
                    : colorGrayDark(docStyle),
                  strokeStyle: datasetVisibility.mailsSent
                    ? colorGreenChartMiddle(docStyle) + 'E6'
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 1,
                  hidden: !datasetVisibility.mailsSent,
                  datasetIndex: datasetIndex++,
                });
              }

              if (activeSecurityCulture) {
                labels.push({
                  text: t('dashboard.activity.overTime.surveysAnswered'),
                  fillStyle: datasetVisibility.surveysAnswered
                    ? colorPurpleText(docStyle) + 'E6'
                    : colorGrayDark(docStyle),
                  strokeStyle: datasetVisibility.surveysAnswered
                    ? colorPurpleText(docStyle) + 'E6'
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 1,
                  hidden: !datasetVisibility.surveysAnswered,
                  datasetIndex: datasetIndex++,
                });
              }

              return labels;
            },
          },
          position: 'bottom' as const,
          align: 'start' as const,
          onClick: (e: any, legendItem: any) => {
            if (legendItem && legendItem.datasetIndex !== undefined) {
              const datasetKeys = [];
              if (activeAwarenessTraining) datasetKeys.push('courseCompletion');
              if (activePhishing) datasetKeys.push('mailsSent');
              if (activeSecurityCulture) datasetKeys.push('surveysAnswered');

              const datasetKey = datasetKeys[legendItem.datasetIndex];
              if (datasetKey) {
                handleLegendClick(datasetKey);
              }
            }
          },
          onHover: (e: any, legendItem: any) => {
            const canvas = e.chart?.canvas;
            if (canvas) {
              canvas.style.cursor = legendItem?.text ? 'pointer' : 'default';
            }
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
            drawOnChartArea: true,
          },
          barPercentage: 0.5,
          categoryPercentage: 0.6,
          border: {
            dash: [5, 5],
            display: false,
          },
        },
        y: {
          min: 0,
          max: roundedMax,
          ticks: {
            color: textColorSecondary,
            stepSize: 40,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
            drawOnChartArea: true,
          },
          border: {
            dash: [5, 5],
            display: false,
          },
        },
      },
      elements: {
        bar: {
          borderRadius: {
            topLeft: 5,
            topRight: 5,
          },
        },
        line: {
          borderDash: [7, 5],
        },
      },
      onHover: (event: any, activeElements: any[]) => {
        if (!event?.native?.target?.style) return;
        event.native.target.style.cursor =
          activeElements.length > 0 ? 'pointer' : 'default';
      },
    };

    setChartData(data);
    setChartOptions(options);
    setIsChartDataLoading(false);
  }, [
    datasets,
    datasetVisibility,
    activeAwarenessTraining,
    activePhishing,
    activeSecurityCulture,
  ]);

  useEffect(() => {
    return () => {
      const tooltipId = 'chart-tooltip-activity-bar-chart';
      hideTooltipForNavigation(tooltipId);
    };
  }, []);

  return (
    <ChartContainer
      id="activity-bar-chart-container"
      $isMainDashboard={isMainDashboard}
    >
      {!isMainDashboard && (
        <AccordionTabHeader
          title={t('dashboard.awareness.completions.perMonth.title')}
          description={t(
            'dashboard.awareness.completions.perMonth.description',
          )}
          isSubHeader
        />
      )}
      <ChartWrapper $isMainDashboard={isMainDashboard}>
        {isLoading || isChartDataLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : hasNoData ? (
          <FlexContainer
            height="100%"
            direction="column"
            justify="center"
            align="center"
          >
            <p className="text-center">{t('phishing.analytics.noData')}</p>
          </FlexContainer>
        ) : (
          <StyledBarChart
            type="line"
            data={chartData}
            options={chartOptions}
            id="activity-bar-chart"
            $fitContainer={!isMainDashboard}
          />
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};
